import { useQuery } from "@tanstack/react-query";
import styled, { keyframes } from "styled-components";
import { useRef, useState } from "react";
import { spacing } from "src/design-system/tokens/spacing";
import { border_radius } from "src/design-system/tokens/border";
import {
  sendAnalyticsInteractionEvent,
  sendAnalyticsNonInteractionEvent,
} from "src/analytics/sendAnalyticsEvent";
import useUser from "src/utils/hooks/useUser";
import useSearch from "src/utils/hooks/useSearch";
import { useApiConfig } from "src/api/ApiConfigProvider";
import color from "src/design-system/tokens/color";
import { useLanguageCode } from "src/utils/hooks/useLanguageCode";
import { screenMinWidth } from "src/theme";
import type { InlineSerpAd } from "../InlineAds/InlineSerpAd/InlineSerpAd";
import { getKoddiDestinationBranding } from "./api";
import { useKoddiEvents } from "./useKoddiEvents";

export function KoddiDestinationBranding({ setHasAd }: InlineSerpAd) {
  const apiConfig = useApiConfig();
  const language = useLanguageCode();
  const { currencyCode } = useUser();
  const { destination } = useSearch();
  const ref = useRef<HTMLDivElement>(null);
  const [imagesReady, setImagesReady] = useState(false);
  const imagesLoadedCount = useRef(0);

  const { data, isLoading, failureCount } = useQuery({
    queryKey: ["koddiDestinationBranding"],
    queryFn: async () => {
      const handleResponse = (isSuccess: boolean) => {
        setHasAd(isSuccess);

        const action = isSuccess ? "Koddi:Render" : "FailedToFill";
        sendAnalyticsNonInteractionEvent({
          category: "Ads",
          action,
          label: "DestinationBranding",
        });
      };

      // Only log requested event on first attempt.
      if (failureCount === 0) {
        sendAnalyticsNonInteractionEvent({
          category: "Ads",
          action: "Koddi:Requested",
          label: "DestinationBranding",
        });
      }

      try {
        const response = await getKoddiDestinationBranding({
          apiConfig,
          language,
          currency: currencyCode,
          destination: destination?.canonicalName ?? "",
        });

        handleResponse(!!response);

        return response;
      } catch (err) {
        handleResponse(false);
        return null;
      }
    },
    refetchOnWindowFocus: false,
  });

  const { sendClickEvent } = useKoddiEvents({
    ref,
    data,
    adType: "DestinationBranding",
    shouldLogImpression: !isLoading,
  });

  if (isLoading || !data) {
    return <></>;
  }

  const { headline, hotel1, hotel2, logo } = data;

  // We want to wait for all images to load before
  // displaying them. Once  ready we grafeully
  // transition them in with a blur effect.
  const handleImageLoaded = () => {
    imagesLoadedCount.current += 1;
    if (imagesLoadedCount.current === 3) {
      setImagesReady(true);
    }
  };

  const handleImgClick = (
    e: React.MouseEvent,
    clickedEl: string,
    href: string,
  ) => {
    // Prevent the click from triggering the ad wrapper click event.
    e.stopPropagation();
    sendClickEvent(clickedEl);

    window.open(href, "_blank");
  };

  return (
    <KoddiAdWrapper
      ref={ref}
      onClick={() => {
        // Whilst Koddi only sends target hrefs for the hotel images,
        // we want to capture when users click on the entire ad itself,
        // as we currently hypothesise this would be a common action
        // in which users would expect the whole panel to be clickable.
        // Note - it does not trigger the Koddi click event.
        // TODO - remove this and / or update design once we have a
        // better understanding of how users interact with this ad.
        sendAnalyticsInteractionEvent({
          category: "Ads",
          action: "Koddi:AdClicked",
          label: "DestinationBranding:Wrapper",
        });
      }}
    >
      <Container>
        <LogoImg
          isLoaded={imagesReady}
          src={logo}
          alt=""
          onLoad={handleImageLoaded}
        />
        <span className="block text-body-md font-medium">{headline}</span>
      </Container>
      <ImgWrapper
        onClick={(e) => {
          handleImgClick(e, "Hotel1", hotel1.link);
        }}
      >
        <HotelImg
          src={hotel1.image}
          alt=""
          isLoaded={imagesReady}
          onLoad={handleImageLoaded}
        />
      </ImgWrapper>
      <ImgWrapper
        onClick={(e) => {
          handleImgClick(e, "Hotel2", hotel2.link);
        }}
      >
        <HotelImg
          src={hotel2.image}
          alt=""
          isLoaded={imagesReady}
          onLoad={handleImageLoaded}
        />
      </ImgWrapper>
    </KoddiAdWrapper>
  );
}

const KoddiAdWrapper = styled.div`
  display: grid;
  grid-template-columns: auto repeat(2, ${spacing.xxxxxl});
  gap: ${spacing.md};
  align-items: center;

  width: 100%;
  padding: ${spacing.lg};
  background-color: ${color.bg.fill.solid};
  cursor: default;

  ${screenMinWidth.md} {
    grid-template-columns: auto repeat(2, ${spacing.xxxxxxl});
    gap: ${spacing.lg};
    padding: ${spacing.xl};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${spacing.sm};
  height: 100%;
`;

const loadImageAnimation = keyframes`
  0% {
    opacity: 0;
    filter: blur(${spacing.sm});
  }
  10% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    filter: blur(0);
  }
`;

const Img = styled.img<{ isLoaded: boolean }>`
  display: block;
  object-fit: cover;

  opacity: 0;
  filter: blur(${spacing.sm});

  animation-duration: 0.8s;
  animation-name: ${loadImageAnimation};
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  animation-play-state: ${({ isLoaded }) => (isLoaded ? "running" : "paused")};
`;

const LogoImg = styled(Img)`
  width: 100%;
  max-width: 132px;
  aspect-ratio: 3/1;
  margin-right: auto;
  // TODO - remove margin-left value when the updated
  // API assets are available.
  margin-left: -12px;

  ${screenMinWidth.md} {
    margin: auto;
    max-width: 172px;
  }
`;

const ImgWrapper = styled.div`
  border-radius: ${border_radius.rounded_md};
  overflow: hidden;
  aspect-ratio: 1/ 1;
  cursor: pointer;
`;

// TODO - remove image offset. It is only there
// to hide the white space on the left & right
// of the assets from Koddi's API.
const IMG_OFFSET = 10;

const HotelImg = styled(Img)`
  transition: transform 0.3s ease-in-out;
  // TODO - update these values when the updated
  // API assets are available.
  margin: -${IMG_OFFSET}px;
  width: calc(100% + ${IMG_OFFSET * 2}px);
  height: calc(100% + ${IMG_OFFSET * 2}px);

  &:hover {
    transform: scale(1.1);
  }
`;
